import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { GlassCard } from './Layout';
import { Target, Check, ArrowUpRight, TrendingUp } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * A test score means nothing on its own. It means something against the schools the
 * person actually saved, which is federal data we already hold.
 */
export const ScoreVsMySchools = () => {
  const { getAuthHeader, user } = useAuth();
  const [saved, setSaved] = useState([]);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/colleges/favorites`, getAuthHeader());
      setSaved((r.data.favorites || []).filter((c) => c.sat_average || c.act_midpoint));
    } catch { /* additive */ }
  }, [getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  const sat = Number(user?.sat_score) || null;
  const act = Number(user?.act_score) || null;
  if (!saved.length) return null;

  const rows = saved.map((c) => {
    const avg = c.sat_average || null;
    const mine = sat;
    const gap = avg && mine ? mine - avg : null;
    const above = gap != null && gap >= 0;
    return { ...c, avg, mine, gap, above };
  }).sort((a, b) => (b.avg || 0) - (a.avg || 0));

  const stretch = rows.filter((r) => r.gap != null && r.gap < 0);
  const target = stretch.length ? Math.max(...stretch.map((r) => Math.abs(r.gap))) : 0;

  return (
    <GlassCard className="mb-6" data-testid="score-vs-schools">
      <div className="flex flex-wrap items-center gap-3">
        <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-cyan-400/15 border border-cyan-300/30">
          <Target className="w-4 h-4 text-cyan-300" />
        </span>
        <div>
          <p className="text-[11px] uppercase tracking-wider text-cyan-300">Your score, against your list</p>
          <h2 className="font-outfit text-lg font-semibold text-white leading-tight">
            {sat
              ? (target > 0
                ? `${target} more points clears every school on your list.`
                : 'You are at or above the average at every school you saved.')
              : 'Add your test score and we will measure it against your list.'}
          </h2>
        </div>
      </div>

      {!sat && (
        <p className="text-sm text-slate-300/85 mt-3">
          Put your SAT or ACT score on your profile and this becomes a straight comparison
          against the average admitted score at each college you have saved.
        </p>
      )}

      <div className="mt-4 space-y-2.5">
        {rows.map((r) => {
          const hue = r.gap == null ? '#94a3b8' : r.above ? '#34d399' : Math.abs(r.gap) <= 60 ? '#fbbf24' : '#f87171';
          const pct = r.avg && r.mine ? Math.min(100, Math.round((r.mine / r.avg) * 100)) : 0;
          return (
            <div key={r.name} className="rounded-xl border p-3"
              style={{ borderColor: `${hue}33`, background: `linear-gradient(150deg, ${hue}10, rgba(255,255,255,0.02))` }}
              data-testid={`score-row-${r.id || r.name}`}>
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <p className="text-sm font-semibold text-white">{r.name}</p>
                <p className="text-xs" style={{ color: hue }}>
                  {r.avg ? <>Average admitted SAT <span className="font-semibold">{r.avg}</span></> : 'No SAT reported'}
                  {r.act_midpoint ? ` · ACT ${r.act_midpoint}` : ''}
                </p>
              </div>

              {r.gap != null && (
                <>
                  <div className="mt-2 h-1.5 rounded-full bg-white/10 overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-500"
                      style={{ width: `${pct}%`, background: hue }} />
                  </div>
                  <p className="text-xs mt-1.5 flex items-center gap-1.5" style={{ color: hue }}>
                    {r.above
                      ? <><Check className="w-3.5 h-3.5" />You are {r.gap} points above their average with {r.mine}.</>
                      : <><TrendingUp className="w-3.5 h-3.5" />You are {Math.abs(r.gap)} points short with {r.mine}. That is the gap to close.</>}
                  </p>
                </>
              )}

              {r.acceptance_rate != null && (
                <p className="text-[11px] text-slate-300 mt-1">
                  Accepts {Math.round(r.acceptance_rate)}% of applicants
                  {r.avg && r.mine && r.above ? ' · your score is not the obstacle here' : ''}
                </p>
              )}
            </div>
          );
        })}
      </div>

      {target > 0 && (
        <p className="text-xs text-slate-300 mt-4">
          Merit money at most public universities is banded by score, so points are worth real
          dollars, not just admission. Price what that changes in{' '}
          <Link to="/app/college-intel" className="text-cyan-300 hover:text-cyan-200 inline-flex items-center gap-1">
            Education investment <ArrowUpRight className="w-3 h-3" />
          </Link>.
        </p>
      )}

      <p className="text-[11px] text-slate-300 mt-2">
        Averages are the federal figure reported by each institution. An average is not a cut-off,         half of admitted students scored below it.
      </p>
    </GlassCard>
  );
};

export default ScoreVsMySchools;
